// The typed `api` client for the Worker's `/api/*` Contract.
//
// Same-origin by design (ADR-0021): the SPA and the Worker live in one Pages
// project, so every request is a *relative* path (`/api/...`) and the **Owner
// Session cookie** rides along automatically (`credentials: "same-origin"`). Under
// ADR-0060 the Worker brokers Google sign-in itself and issues its own opaque,
// HttpOnly Session cookie (Cloudflare Access is retired from the human lane). There
// is no token handling in the SPA — it never sees or stores the Session token; the
// cookie does the rest, and the login/logout round-trips live on `/auth/*`.
//
// Errors are normalized into `ApiError`. The one the UI cares about most is 401:
// the Session is missing or expired, so the app must send the owner back through
// Google sign-in (see `isUnauthorized` + the App shell's re-login affordance).

import type {
  AlbumPublishState,
  AlbumSummary,
  AlbumView,
  ArchiveItem,
  ClaimCheck,
  ClaimResult,
  Device,
  DevicePage,
  Look,
  LookSummary,
  Me,
  MediaItem,
  MediaPage,
  MediaTypeFilter,
  ReferenceLoop,
  RevokeResult,
  GuestEntry,
  GuestCredentials,
} from "./types";

/**
 * Client-wide request timeout (ms). A stalled fetch (connection opened, no response,
 * no error) is aborted after this so it surfaces as an `ApiError` instead of hanging a
 * caller's in-flight guard forever — the wedge `useMedia` would otherwise never recover
 * from without a reload (R26).
 */
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Merge an optional caller signal with the client's timeout signal into ONE signal that
 * aborts when either fires (no dependency on `AbortSignal.any` for portability). The
 * caller signal lets a hook abort its in-flight request on unmount; the timeout signal
 * bounds every request.
 */
function combineSignals(caller: AbortSignal | undefined, timeout: AbortSignal): AbortSignal {
  if (!caller) return timeout;
  const merged = new AbortController();
  if (caller.aborted) merged.abort(caller.reason);
  else if (timeout.aborted) merged.abort(timeout.reason);
  else {
    caller.addEventListener("abort", () => merged.abort(caller.reason), { once: true });
    timeout.addEventListener("abort", () => merged.abort(timeout.reason), { once: true });
  }
  return merged.signal;
}

/** A failed `/api/*` request: the HTTP status plus the Worker's `{ error }` message. */
export class ApiError extends Error {
  constructor(
    /** The HTTP status code (e.g. 401, 404, 500). */
    readonly status: number,
    /** The Worker's `error` message, or a generic fallback. */
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the Session is missing/expired and the owner must re-auth (Google sign-in). */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** Convenience for callers that catch `unknown`: is this an auth failure? */
export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.isUnauthorized;
}

/** Options for `getMedia` — the `/api/media` cursor + page-size query. */
export interface GetMediaParams {
  /** Continuation cursor from a prior page; omit/null for the first page. */
  cursor?: string | null;
  /** Page size (1..100; the Worker clamps to its `MEDIA_PAGE_SIZE`). */
  limit?: number;
  /** Type filter — "all" (default), "photos" (Captures), or "clips". */
  type?: MediaTypeFilter;
  /** Optional deviceId scope — narrow to one camera. */
  device?: string | null;
  /** Archive visibility (ADR-0058): false/undefined → the default gallery; true → the Archived view. */
  archived?: boolean;
  /** Abort signal so a hook can cancel an in-flight page fetch on unmount (R26). */
  signal?: AbortSignal;
}

/** Args for `claimApprove` — the `POST /api/claim/approve` body (B2, #63). */
export interface ClaimApproveParams {
  /** The human-readable Claim code the Owner read off the camera's QR/screen. */
  userCode: string;
  /** An optional name for the camera; the Worker trims/caps it (empty → no name). */
  name?: string | null;
}

/** Injectable seam so tests can drive the client without a real network. */
export interface ApiClientOptions {
  /** Base path prepended to every endpoint. Default "" → same-origin relative `/api/*`. */
  baseUrl?: string;
  /** The `fetch` implementation. Default: the global `fetch`. */
  fetch?: typeof fetch;
  /** Per-request stall timeout (ms) before the client aborts. Default `REQUEST_TIMEOUT_MS`. */
  timeoutMs?: number;
}

export interface ApiClient {
  /**
   * `GET /api/me` → whether the signed-in subject is an Owner (`member: true` + identity)
   * or a signed-in non-member (`member: false`, the join wall). Throws `ApiError` 401 when
   * there is no valid Session — the app then shows the public Landing (ADR-0060).
   */
  getMe(): Promise<Me>;
  /**
   * `POST /auth/logout` → delete the Session server-side + clear its cookie. Same-origin
   * (the cookie rides along). The app then drops to the signed-out (Landing) state.
   */
  logout(): Promise<void>;
  /**
   * `GET /claim/check?code=` (public, no Session needed) → whether a Claim code can still
   * be approved, so the claim screen can gate the "Continue with Google" round-trip on a
   * live code. A 429 (rate-limited) is mapped to `{ ok: false, reason: "rate_limited" }`
   * rather than thrown, so a hammered pre-check reads as "try later", not a hard error.
   */
  checkClaimCode(code: string): Promise<ClaimCheck>;
  /** `GET /api/media?cursor=&limit=` → one page of the Owner's media. */
  getMedia(params?: GetMediaParams): Promise<MediaPage>;
  /**
   * `POST /api/media/archive` → hide the given Media cards from the gallery (reversible; ADR-0058).
   * Pass their `key`s; the client derives `{deviceId,name}` and the Worker composes the object_key
   * from the Session-resolved owner. Idempotent; returns how many were newly archived.
   */
  archiveMedia(keys: string[]): Promise<{ archived: number }>;
  /** `POST /api/media/restore` → un-hide the given Media cards (the inverse; ADR-0058). Returns how many were restored. */
  restoreMedia(keys: string[]): Promise<{ restored: number }>;
  /**
   * `POST /api/claim/approve` → bind a pending Claim code to this Owner, naming
   * the camera. Throws `ApiError` 404 (unknown code), 409 (already claimed/used),
   * or 410 (expired) — the claim screen surfaces each (B2, #63).
   */
  claimApprove(params: ClaimApproveParams): Promise<ClaimResult>;
  /** `GET /api/devices` → the Owner's cameras + status (own rows only). */
  getDevices(): Promise<Device[]>;
  /** `POST /api/devices/:id/revoke` → flip an owned camera → `revoked`. */
  revokeDevice(deviceId: string): Promise<RevokeResult>;
  /** `POST /api/devices/:id/looks` → commit a Look into the camera's library; returns its id. */
  commitLook(deviceId: string, look: Look): Promise<{ id: string }>;
  /** `PUT /api/devices/:id/look/test` → save the editor WIP as the Test look (Look Sync pulls it live, #107). */
  saveTestLook(deviceId: string, look: Look): Promise<void>;
  /** `GET /api/devices/:id/look/test` → the saved editor WIP (Test look), or null if none saved yet — so
   *  the editor resumes the owner's last edit on load instead of starting from defaults. */
  getTestLook(deviceId: string): Promise<Look | null>;
  /** `GET /api/devices/:id/looks` → the camera's committed Looks (id + name + hash). */
  listLooks(deviceId: string): Promise<LookSummary[]>;
  /** `GET /api/devices/:id/looks/:lookId` → one committed Look's full JSON (re-open in the editor). */
  getLook(deviceId: string, lookId: string): Promise<Look>;
  /** `POST /api/devices/:id/looks/:lookId/rename` → re-key a Look under a new name. */
  renameLook(deviceId: string, lookId: string, name: string): Promise<{ id: string }>;
  /** `DELETE /api/devices/:id/looks/:lookId` → drop a Look (the camera mirrors the removal). */
  deleteLook(deviceId: string, lookId: string): Promise<void>;
  /** `POST /api/devices/:id/looks/:lookId/archive` → shelve a Look: kept in the portal, dropped from the camera (ADR-0057). */
  archiveLook(deviceId: string, lookId: string): Promise<void>;
  /** `POST /api/devices/:id/looks/:lookId/unarchive` → return a shelved Look to the camera. Throws `ApiError` 409 when the camera is full. */
  unarchiveLook(deviceId: string, lookId: string): Promise<void>;
  /** `GET /api/devices/:id/reference-loop` → the camera's Reference loop frames (presigned URLs). */
  getReferenceLoop(deviceId: string): Promise<ReferenceLoop>;
  /** `GET /api/guests` → the Owner's guest grants with live sessions + Looks counts (owner-only). */
  listGuests(): Promise<GuestEntry[]>;
  /** `POST /api/guests` → mint a guest; the response is the ONLY place the password ever appears. */
  createGuest(user: string): Promise<GuestCredentials>;
  /** `POST /api/guests/:user/rotate` → a fresh password (shown once); older sessions stop resolving. */
  rotateGuest(user: string): Promise<GuestCredentials>;
  /** `POST /api/guests/:user/disable|enable` → the instant off/on switch. */
  setGuestDisabled(user: string, disabled: boolean): Promise<void>;
  /** `POST /api/guests/:user/revoke-sessions` → sign the guest out everywhere, grant kept. */
  revokeGuestSessions(user: string): Promise<void>;
  /** `DELETE /api/guests/:user` → remove the grant and its sessions. */
  deleteGuest(user: string): Promise<void>;
  /** `GET /api/albums` → the Owner's Albums (ADR-0059). Unwraps the `{ albums }` envelope to the bare array. */
  listAlbums(): Promise<AlbumSummary[]>;
  /** `POST /api/albums` `{ name }` → create an Album; returns its server-minted id. */
  createAlbum(name: string): Promise<{ id: string }>;
  /** `GET /api/albums/:id` → one Album's summary + its ordered items (AlbumView). */
  getAlbum(id: string): Promise<AlbumView>;
  /** `POST /api/albums/:id/rename` `{ name }` → re-name an Album in place. */
  renameAlbum(id: string, name: string): Promise<void>;
  /** `DELETE /api/albums/:id` → delete an Album (its items are untouched — an Album is only a curated view). */
  deleteAlbum(id: string): Promise<void>;
  /** `POST /api/albums/:id/items` `{ keys }` → add media (by R2 key) to an Album; returns how many were newly added + the new total. */
  addToAlbum(id: string, keys: string[]): Promise<{ added: number; total: number }>;
  /** `POST /api/albums/:id/items/remove` `{ key }` → remove one item from an Album (the media itself is untouched). */
  removeFromAlbum(id: string, key: string): Promise<void>;
  /** `POST /api/albums/:id/reorder` `{ order }` → set the Album's item order (the full ordered list of keys). */
  reorderAlbum(id: string, order: string[]): Promise<void>;
  /**
   * `POST /api/albums/:id/share` → mint the public /a/<token> link and ENQUEUE the publish-copy
   * (ADR-0061). Returns **immediately** with the token + path + `publishState:"publishing"` — the
   * copy runs on a queue, so a resolved promise does NOT mean the link is ready. Callers must
   * re-read the album (`getAlbum`) and reveal the link only once it is `shared` + `idle`.
   * Re-calling it on a `failed` album is the Retry (the copy resumes from what's already copied).
   */
  shareAlbum(id: string): Promise<{ token: string; path: string; publishState?: AlbumPublishState }>;
  /** `POST /api/albums/:id/unshare` → revert to private + kill the public link. */
  unshareAlbum(id: string): Promise<void>;
  /** `POST /api/albums/:id/slug` `{ slug }` → set the cosmetic link slug (empty string clears it → name-derived). */
  setAlbumSlug(id: string, slug: string): Promise<void>;
  /** `POST /api/albums/:id/cover` `{ key }` → set the cover item (page header + OG image; empty clears → first item). */
  setAlbumCover(id: string, key: string): Promise<void>;
}

/**
 * Build the typed client. `baseUrl` defaults to "" so requests are same-origin
 * relative paths; tests pass a stub `fetch` (and optionally a `baseUrl`) to assert
 * request shaping and error handling against the Contract.
 */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? "";
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;

  // The one request path: carry the Owner Session cookie, normalize errors into
  // `ApiError`, and bound every call by a timeout (R26). `body === undefined` ⇒ no
  // content-type + no body (a GET, or an empty-bodied POST). An optional caller `signal`
  // lets a hook abort on unmount; the client's own timeout signal is merged in so a stall
  // aborts and surfaces as an error instead of hanging forever.
  async function send<T>(
    method: string,
    path: string,
    opts: { body?: unknown; signal?: AbortSignal } = {},
  ): Promise<T> {
    const { body, signal } = opts;
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), timeoutMs);
    try {
      const res = await doFetch(`${baseUrl}${path}`, {
        method,
        // Same-origin in production; harmless in dev.
        credentials: "same-origin",
        headers:
          body === undefined
            ? { accept: "application/json" }
            : { accept: "application/json", "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: combineSignals(signal, timeout.signal),
      });
      if (!res.ok) {
        throw new ApiError(res.status, await errorMessage(res));
      }
      return (await res.json()) as T;
    } catch (err) {
      // A fired timeout is our own abort — surface it as a real error (status 0) rather
      // than leaking a raw AbortError. A caller-initiated abort (hook unmount) propagates
      // as-is so the hook can recognize + ignore its own cancellation.
      if (timeout.signal.aborted) throw new ApiError(0, "request timed out");
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  function request<T>(path: string, signal?: AbortSignal): Promise<T> {
    return send<T>("GET", path, { signal });
  }
  // The write counterpart of `request`: POST a JSON body (`body` undefined for an empty POST).
  function postJson<T>(path: string, body?: unknown): Promise<T> {
    return send<T>("POST", path, { body });
  }
  // POST / DELETE with the same auth + normalization; `request` covers GET.
  function sendJson<T>(method: string, path: string, body?: unknown): Promise<T> {
    return send<T>(method, path, { body });
  }

  // Archive/Restore batch limit — mirrors the Worker's ARCHIVE_BATCH_MAX (cloud/src/lib/archive.ts).
  // "Select all → Archive" can exceed it, so chunk client-side and sum the affected counts rather
  // than let one oversized POST 400.
  const ARCHIVE_BATCH_LIMIT = 500;
  async function batchMutate(path: string, keys: string[]): Promise<number> {
    let total = 0;
    for (let i = 0; i < keys.length; i += ARCHIVE_BATCH_LIMIT) {
      const items = keys.slice(i, i + ARCHIVE_BATCH_LIMIT).map(keyToArchiveItem);
      const r = await postJson<{ archived?: number; restored?: number }>(path, { items });
      total += r.archived ?? r.restored ?? 0;
    }
    return total;
  }

  return {
    getMe() {
      return request<Me>("/api/me");
    },
    logout() {
      // On the /auth/* lane (not /api/*), but the same send path carries the cookie +
      // normalizes errors. Discard the `{ ok }` body — the caller only needs completion.
      return postJson<{ ok: boolean }>("/auth/logout").then(() => undefined);
    },
    checkClaimCode(code: string) {
      return request<ClaimCheck>(
        `/claim/check?code=${encodeURIComponent(code)}`,
      ).catch((e: unknown): ClaimCheck => {
        // The endpoint returns 429 when rate-limited; surface it as a normal negative
        // result (with a reason) instead of throwing, so the screen can say "try later".
        if (e instanceof ApiError && e.status === 429) {
          return { ok: false, reason: "rate_limited" };
        }
        throw e;
      });
    },
    getMedia(params: GetMediaParams = {}) {
      return request<MediaPage>(`/api/media${mediaQuery(params)}`, params.signal);
    },
    archiveMedia(keys: string[]) {
      return batchMutate("/api/media/archive", keys).then((archived) => ({ archived }));
    },
    restoreMedia(keys: string[]) {
      return batchMutate("/api/media/restore", keys).then((restored) => ({ restored }));
    },
    claimApprove(params: ClaimApproveParams) {
      // The Worker's body keys are snake_case (`user_code`); the SPA's params are
      // camelCase. `name` is omitted entirely when not provided.
      const payload: { user_code: string; name?: string } = {
        user_code: params.userCode,
      };
      if (params.name != null) payload.name = params.name;
      return postJson<ClaimResult>("/api/claim/approve", payload);
    },
    getDevices() {
      // The Worker wraps the list in `{ devices }`; the SPA returns the bare array.
      return request<DevicePage>("/api/devices").then((p) => p.devices);
    },
    revokeDevice(deviceId: string) {
      return postJson<RevokeResult>(
        `/api/devices/${encodeURIComponent(deviceId)}/revoke`,
      );
    },
    commitLook(deviceId, look) {
      return sendJson<{ id: string }>("POST", `/api/devices/${encodeURIComponent(deviceId)}/looks`, look);
    },
    saveTestLook(deviceId, look) {
      return sendJson<{ ok: boolean }>(
        "PUT",
        `/api/devices/${encodeURIComponent(deviceId)}/look/test`,
        look,
      ).then(() => undefined);
    },
    getTestLook(deviceId) {
      return request<Look>(
        `/api/devices/${encodeURIComponent(deviceId)}/look/test`,
      ).catch((e: unknown): null => {
        if (e instanceof ApiError && e.status === 404) return null; // fresh owner — no WIP saved yet
        throw e;
      });
    },
    listLooks(deviceId) {
      return request<{ looks: LookSummary[] }>(
        `/api/devices/${encodeURIComponent(deviceId)}/looks`,
      ).then((m) => m.looks);
    },
    getLook(deviceId, lookId) {
      return request<Look>(
        `/api/devices/${encodeURIComponent(deviceId)}/looks/${encodeURIComponent(lookId)}`,
      );
    },
    renameLook(deviceId, lookId, name) {
      return sendJson<{ id: string }>(
        "POST",
        `/api/devices/${encodeURIComponent(deviceId)}/looks/${encodeURIComponent(lookId)}/rename`,
        { name },
      );
    },
    deleteLook(deviceId, lookId) {
      return sendJson<{ ok: boolean }>(
        "DELETE",
        `/api/devices/${encodeURIComponent(deviceId)}/looks/${encodeURIComponent(lookId)}`,
      ).then(() => undefined);
    },
    listGuests() {
      return sendJson<{ guests: GuestEntry[] }>("GET", "/api/guests").then((r) => r.guests);
    },
    createGuest(user) {
      return sendJson<GuestCredentials>("POST", "/api/guests", { user });
    },
    rotateGuest(user) {
      return sendJson<GuestCredentials>("POST", `/api/guests/${encodeURIComponent(user)}/rotate`);
    },
    setGuestDisabled(user, disabled) {
      return sendJson<{ ok: boolean }>(
        "POST",
        `/api/guests/${encodeURIComponent(user)}/${disabled ? "disable" : "enable"}`,
      ).then(() => undefined);
    },
    revokeGuestSessions(user) {
      return sendJson<{ ok: boolean }>(
        "POST",
        `/api/guests/${encodeURIComponent(user)}/revoke-sessions`,
      ).then(() => undefined);
    },
    deleteGuest(user) {
      return sendJson<{ ok: boolean }>(
        "DELETE",
        `/api/guests/${encodeURIComponent(user)}`,
      ).then(() => undefined);
    },
    archiveLook(deviceId, lookId) {
      return sendJson<{ ok: boolean }>(
        "POST",
        `/api/devices/${encodeURIComponent(deviceId)}/looks/${encodeURIComponent(lookId)}/archive`,
      ).then(() => undefined);
    },
    unarchiveLook(deviceId, lookId) {
      return sendJson<{ ok: boolean }>(
        "POST",
        `/api/devices/${encodeURIComponent(deviceId)}/looks/${encodeURIComponent(lookId)}/unarchive`,
      ).then(() => undefined);
    },
    getReferenceLoop(deviceId) {
      return request<ReferenceLoop>(
        `/api/devices/${encodeURIComponent(deviceId)}/reference-loop`,
      );
    },
    listAlbums() {
      // The Worker wraps the list in `{ albums }`; the SPA returns the bare array (mirrors getDevices).
      return request<{ albums: AlbumSummary[] }>("/api/albums").then((r) => r.albums);
    },
    createAlbum(name) {
      // Discard the `{ ok }` flag — the caller only needs the new id.
      return postJson<{ ok: boolean; id: string }>("/api/albums", { name }).then(
        ({ id }) => ({ id }),
      );
    },
    getAlbum(id) {
      return request<AlbumView>(`/api/albums/${encodeURIComponent(id)}`);
    },
    renameAlbum(id, name) {
      return sendJson<{ ok: boolean }>(
        "POST",
        `/api/albums/${encodeURIComponent(id)}/rename`,
        { name },
      ).then(() => undefined);
    },
    deleteAlbum(id) {
      return sendJson<{ ok: boolean }>(
        "DELETE",
        `/api/albums/${encodeURIComponent(id)}`,
      ).then(() => undefined);
    },
    addToAlbum(id, keys) {
      return postJson<{ ok: boolean; added: number; total: number }>(
        `/api/albums/${encodeURIComponent(id)}/items`,
        { keys },
      ).then(({ added, total }) => ({ added, total }));
    },
    removeFromAlbum(id, key) {
      return postJson<{ ok: boolean }>(
        `/api/albums/${encodeURIComponent(id)}/items/remove`,
        { key },
      ).then(() => undefined);
    },
    reorderAlbum(id, order) {
      return postJson<{ ok: boolean }>(
        `/api/albums/${encodeURIComponent(id)}/reorder`,
        { order },
      ).then(() => undefined);
    },
    shareAlbum(id) {
      return postJson<{ ok: boolean; token: string; path: string; publishState?: AlbumPublishState }>(
        `/api/albums/${encodeURIComponent(id)}/share`,
      ).then(({ token, path, publishState }) => ({ token, path, publishState }));
    },
    unshareAlbum(id) {
      return postJson<{ ok: boolean }>(
        `/api/albums/${encodeURIComponent(id)}/unshare`,
      ).then(() => undefined);
    },
    setAlbumSlug(id, slug) {
      return postJson<{ ok: boolean }>(
        `/api/albums/${encodeURIComponent(id)}/slug`,
        { slug },
      ).then(() => undefined);
    },
    setAlbumCover(id, key) {
      return postJson<{ ok: boolean }>(
        `/api/albums/${encodeURIComponent(id)}/cover`,
        { key },
      ).then(() => undefined);
    },
  };
}

/** Build the `/api/media` query string from cursor + limit, omitting empty values. */
function mediaQuery(params: GetMediaParams): string {
  const q = new URLSearchParams();
  if (params.cursor != null && params.cursor !== "") {
    q.set("cursor", params.cursor);
  }
  if (params.limit != null) {
    q.set("limit", String(params.limit));
  }
  // "all" is the default — omit it so the URL stays clean for the common case.
  if (params.type != null && params.type !== "all") {
    q.set("type", params.type);
  }
  if (params.device != null && params.device !== "") {
    q.set("device", params.device);
  }
  // "false" is the default (the normal gallery) — omit it; only the Archived view sets it.
  if (params.archived) {
    q.set("archived", "1");
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * Derive the Archive request item from a Media card's R2 key ({userId}/{deviceId}/{name}, ADR-0058).
 * The client sends only `{deviceId,name}`; the Worker composes the full object_key from the
 * Session-resolved owner, so a client can never name another owner's media.
 */
function keyToArchiveItem(key: string): ArchiveItem {
  const parts = key.split("/");
  const name = parts[parts.length - 1] ?? "";
  const deviceId = parts[parts.length - 2] ?? "";
  return { deviceId, name };
}

/** Extract the Worker's `{ error }` message, tolerating a non-JSON error body. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body?.error === "string" && body.error) return body.error;
  } catch {
    // Non-JSON / empty body — fall through to a status-derived message.
  }
  return `request failed (${res.status})`;
}

/**
 * Sort a media page newest-first by **capture order** — the shared IMG/GIF `####`
 * index in the key (higher = captured later), NOT `uploaded`. `uploaded` is the cloud
 * add-time, and the camera syncs newest-captured first, so ordering by it inverts the
 * gallery (older shots first). The Worker already returns capture-newest-first; this
 * keeps the accumulated, re-sorted list in the same order. Ties: added-time, then key.
 */
export function sortNewestFirst(items: readonly MediaItem[]): MediaItem[] {
  return [...items].sort((a, b) => {
    const ci = captureIndex(b.key) - captureIndex(a.key);
    if (ci !== 0) return ci;
    if (b.uploaded !== a.uploaded) return b.uploaded - a.uploaded;
    return b.key.localeCompare(a.key);
  });
}

/** The capture index in a media key (the shared IMG/GIF `####` counter; higher =
 *  newer). -1 if absent, so a malformed key sorts oldest. */
function captureIndex(key: string): number {
  const m = /_(\d+)\.[a-z0-9]+$/i.exec(key);
  return m ? Number.parseInt(m[1]!, 10) : -1;
}
